import { apiClient } from "@/api";
import type { ApiResponse, PaginatedData } from "@/types";
import type { AxiosRequestConfig } from "axios";

export interface CreateInquiryPayload {
  title: string;
  content: string;
  agent_ids: string[];
  project_id?: string;
  selected_consulting?: string;
}

export interface InquiryData {
  id: string;
  user_id: string;
  title: string;
  content: string;
  status: string;
  admin_id: string;
  created_at: string;
  updated_at: string;
}

export async function createInquiry(payload: CreateInquiryPayload, config?: AxiosRequestConfig): Promise<InquiryData> {
  const { data } = await apiClient.post<ApiResponse<InquiryData>>("/inquiries/", payload, config);
  return data.data!;
}

export async function listInquiries(
  page = 1,
  pageSize = 10,
  config?: AxiosRequestConfig,
): Promise<PaginatedData<InquiryData>> {
  const { data } = await apiClient.get<ApiResponse<PaginatedData<InquiryData>>>("/inquiries/", {
    ...config,
    params: { page, page_size: pageSize },
  });
  return data.data!;
}

export async function deleteInquiry(inquiry_id: string, config?: AxiosRequestConfig): Promise<void> {
  await apiClient.delete(`/inquiries/${inquiry_id}`, config);
}

export interface InquiryReply {
  id: string;
  inquiry_id: string;
  sender_id: string;
  sender_role: "user" | "admin";
  sender_name: string;
  content: string;
  created_at: string;
}

export interface InquiryUserInfo {
  requester_name: string;
  email: string;
  phone: string;
  position: string;
}

export interface InquiryAgentInfo {
  id: string;
  name: string;
  provider: string | null;
}

export interface InquiryDetail extends InquiryData {
  user_info: InquiryUserInfo;
  admin_name: string | null;
  last_reply_at: string | null;
  agents: InquiryAgentInfo[];
  messages: InquiryReply[];
  selected_consulting: string | null;
}

export async function getInquiryDetail(inquiryId: string, config?: AxiosRequestConfig): Promise<InquiryDetail> {
  const { data } = await apiClient.get<ApiResponse<InquiryDetail>>(`/inquiries/${inquiryId}`, config);
  return data.data!;
}

export async function sendInquiryReply(inquiryId: string, content: string, config?: AxiosRequestConfig): Promise<InquiryReply> {
  const { data } = await apiClient.post<ApiResponse<InquiryReply>>(
    `/inquiries/${inquiryId}/messages`,
    { content },
    config
  );
  return data.data!;
}
